if (!customElements.get('accordion-select')) {
  class AccordionSelectElement extends HTMLElement {
    constructor() {
      super();
      this.button = null;
      this.content = null;
      this.isExpanded = false;
    }

    connectedCallback() {
      this.init();
    }

    init() {
      // Find accordion elements within this custom element
      this.button = this.querySelector('.accordion__button');
      this.content = this.querySelector('.accordion__content');

      if (!this.button || !this.content) return;

      this.setupEventListeners();
      this.setAttribute('role', 'group');
    }

    setupEventListeners() {
      // Click event
      this.button.addEventListener('click', (e) => this.handleClick(e));

      // Keyboard navigation
      this.button.addEventListener('keydown', (e) => this.handleKeydown(e));

      // Focus management
      this.addEventListener('focusin', (e) => this.handleFocusIn(e));
      if (this.dataset.allowFocusOut) return;
      this.addEventListener('focusout', (e) => this.handleFocusOut(e));
    }

    handleClick(e) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      this.toggle();
    }

    handleKeydown(e) {
      switch (e.key) {
        case 'Enter':
        case ' ':
          e.preventDefault();
          this.toggle();
          break;
        case 'Escape':
          if (this.isExpanded) {
            e.preventDefault();
            this.close();
            this.button.focus();
          }
          break;
        case 'ArrowDown':
        case 'ArrowUp':
          e.preventDefault();
          this.navigateToSibling(e.key === 'ArrowDown');
          break;
      }
    }

    handleFocusIn(e) {
      // When focus enters this accordion, close other accordions
      if (e.target === this.button || this.content.contains(e.target)) {
        this.closeOtherAccordions();
      }
    }

    handleFocusOut(e) {
      // Check if focus is leaving this accordion completely
      if (this.dataset.accordionMultipleOpenings !== undefined) return;
      const focusLeavingAccordion = !this.contains(e.relatedTarget);

      if (focusLeavingAccordion && this.isExpanded) {
        // Small delay to allow for focus changes, then close if still unfocused
        setTimeout(() => {
          if (!this.contains(document.activeElement)) {
            this.close();
          }
        }, 100);
      }
    }

    navigateToSibling(moveDown) {
      const allAccordions = Array.from(document.querySelectorAll('accordion-select'));
      const currentIndex = allAccordions.indexOf(this);

      let nextIndex;
      if (moveDown) {
        nextIndex = currentIndex + 1 >= allAccordions.length ? 0 : currentIndex + 1;
      } else {
        nextIndex = currentIndex - 1 < 0 ? allAccordions.length - 1 : currentIndex - 1;
      }

      const nextAccordion = allAccordions[nextIndex];
      if (nextAccordion && nextAccordion.button) {
        nextAccordion.button.focus();
      }
    }

    closeOtherAccordions() {
      // Close all other accordion-select elements
      const allAccordions = document.querySelectorAll('accordion-select:not([data-accordion-multiple-openings])');
      allAccordions.forEach(accordion => {
        if (accordion !== this && accordion.isExpanded) {
          accordion.close();
        }
      });
    }

    open() {
      if (this.isExpanded) return;

      this.closeOtherAccordions();

      this.isExpanded = true;
      this.button.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('aria-hidden', 'false');
      this.content.classList.add('active');

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('accordion:open', {
        bubbles: true,
        detail: { accordion: this }
      }));
    }

    close() {
      if (!this.isExpanded) return;

      this.isExpanded = false;
      this.button.setAttribute('aria-expanded', 'false');
      this.content.setAttribute('aria-hidden', 'true');
      this.content.classList.remove('active');

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('accordion:close', {
        bubbles: true,
        detail: { accordion: this }
      }));
    }

    toggle() {
      if (this.isExpanded) {
        this.close();
      } else {
        this.open();
      }
    }

    // Public methods
    isAccordionExpanded() {
      return this.isExpanded;
    }

    // Static utility methods
    static closeAll() {
      const accordions = document.querySelectorAll('accordion-select');
      accordions.forEach(accordion => accordion.close());
    }

    static getOpenAccordions() {
      const accordions = document.querySelectorAll('accordion-select');
      return Array.from(accordions).filter(accordion => accordion.isExpanded);
    }
  }

  // Register the custom element
  customElements.define('accordion-select', AccordionSelectElement);
}
